import React, { useState, useEffect, useRef } from 'react';
import { GoogleMap, DirectionsRenderer, Marker } from '@react-google-maps/api';

// Define a default fallback center to prevent crashes if propCenter is unexpectedly null/undefined
const DEFAULT_FALLBACK_CENTER: google.maps.LatLngLiteral = { lat: 0, lng: 0 };

// Define the isValidCoordinate function locally instead of importing it
const isValidCoordinate = (coord: any): boolean => {
  if (!coord) return false;
  const { lat, lng } = coord;
  return (
    typeof lat === 'number' && 
    typeof lng === 'number' && 
    !isNaN(lat) && 
    !isNaN(lng) &&
    lat >= -90 && 
    lat <= 90 && 
    lng >= -180 && 
    lng <= 180
  );
};

interface GoogleMapComponentProps {
  center: google.maps.LatLngLiteral; // Initial center for the map
  zoom: number; // Initial zoom for the map
  pickupCoords: google.maps.LatLngLiteral | null; // For the pickup marker
  destinationCoords: google.maps.LatLngLiteral | null; // For the destination marker
  maxDistanceKm?: number; // Optional: For the pickup radius circle (not currently used)
  mapContainerClassName?: string;
  pickupAddress?: string | null;       // Input for directions calculation
  destinationAddress?: string | null;  // Input for directions calculation
}

const GoogleMapComponent: React.FC<GoogleMapComponentProps> = ({
  center: propCenter, // Renamed to avoid conflict with internal map center state
  zoom: propZoom,     // Renamed to avoid conflict with internal map zoom state
  pickupCoords,
  destinationCoords,
  pickupAddress,
  destinationAddress,
  maxDistanceKm,
  mapContainerClassName = "w-full h-full", // MODIFIED: Use h-full for reliable height
}) => {  
  const mapRef = useRef<google.maps.Map | null>(null);
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  // Ensure currentMapCenter is always initialized with a valid LatLngLiteral
  const [currentMapCenter, setCurrentMapCenter] = useState<google.maps.LatLngLiteral>(propCenter || DEFAULT_FALLBACK_CENTER);
  const [currentZoom, setCurrentZoom] = useState<number>(propZoom);
  const [mapLoaded, setMapLoaded] = useState<boolean>(false); // Track whether map is fully loaded
  const [internalDirections, setInternalDirections] = useState<google.maps.DirectionsResult | null>(null);
  const [routeError, setRouteError] = useState<string | null>(null);
  // Enhanced validation utility function for the component
  const areCoordinatesValidForRoute = (): boolean => {
    // Check if we have both coordinates
    if (!pickupCoords || !destinationCoords) {
      console.log('Missing coordinates for route', { 
        hasPickup: !!pickupCoords, 
        hasDestination: !!destinationCoords 
      });
      return false;
    }
    
    // Validate pickup coordinates
    if (!isValidCoordinate(pickupCoords)) {
      console.log('Invalid pickup coordinates', pickupCoords);
      return false;
    }
    
    // Validate destination coordinates
    if (!isValidCoordinate(destinationCoords)) {
      console.log('Invalid destination coordinates', destinationCoords);
      return false;
    }
    
    // All checks passed
    return true;
  };

  // Effect to update internal map view state when center/zoom props change
  useEffect(() => {
    if (propCenter) {
      setCurrentMapCenter(propCenter);
    } else if (pickupCoords) {
      setCurrentMapCenter(pickupCoords);
    } else {
      setCurrentMapCenter(DEFAULT_FALLBACK_CENTER);
    }
    setCurrentZoom(propZoom);
  }, [propCenter, propZoom, pickupCoords]);
  // Enhanced effect to clear directions when either pickup or destination is missing or invalid
  useEffect(() => {
    // Check if coordinates are missing or invalid
    const hasValidPickup = pickupCoords && isValidCoordinate(pickupCoords);
    const hasValidDestination = destinationCoords && isValidCoordinate(destinationCoords);
    
    if (!hasValidPickup || !hasValidDestination) {
      console.log('Clearing directions due to invalid coordinates', {
        hasValidPickup,
        hasValidDestination,
        pickupCoords,
        destinationCoords
      });
      
      // Clear directions and error state
      setInternalDirections(null);
      setRouteError(null);
    }
  }, [pickupCoords, destinationCoords]);
  
  // Effect to validate route whenever coordinates change
  useEffect(() => {
    if (!areCoordinatesValidForRoute()) {
      console.log('Coordinates invalid for route, clearing directions');
      if (internalDirections) {
        setInternalDirections(null);
      }
    }
  }, [pickupCoords, destinationCoords]);

  // Effect to reset directions when coordinates are removed or become invalid
  useEffect(() => {
    if (!areCoordinatesValidForRoute()) {
      console.log('Coordinates invalid for route, clearing directions');
      if (internalDirections) {
        setInternalDirections(null);
      }
    }
  }, [pickupCoords, destinationCoords]);

  // Effect to calculate route when pickup/destination coords change
  useEffect(() => {    if (!mapLoaded || !mapInstance) {
      console.log('Map not yet loaded or instance not available');
      return;
    }

    // Skip calculation if either pickup or destination is missing or invalid
    if (!areCoordinatesValidForRoute()) {
      console.log('Skipping directions calculation due to missing or invalid coordinates', {
        pickupExists: !!pickupCoords,
        destinationExists: !!destinationCoords,
        pickupValid: pickupCoords ? isValidCoordinate(pickupCoords) : false,
        destinationValid: destinationCoords ? isValidCoordinate(destinationCoords) : false
      });
      return;
    }
    
    try {
      // Validate Google Maps API availability
      if (!window.google || !window.google.maps || !window.google.maps.DirectionsService) {
        console.error('GoogleMapComponent: Google Maps API not available for DirectionsService.');
        setRouteError('Google Maps API not ready for directions.');
        return;
      }

      const directionsService = new window.google.maps.DirectionsService();
      
      // Validate coordinates
      if (!isValidCoordinate(pickupCoords) || !isValidCoordinate(destinationCoords)) {
        console.error('Invalid coordinates for directions request', {
          pickup: pickupCoords,
          destination: destinationCoords
        });
        setRouteError('Invalid coordinates for directions request');
        return;
      }
      
      console.log('Requesting directions from:', pickupCoords, 'to:', destinationCoords);
      
      directionsService.route(
        {
          origin: pickupCoords,
          destination: destinationCoords,
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === window.google.maps.DirectionsStatus.OK && result) {
            console.log('GoogleMapComponent: Directions received successfully');
            setInternalDirections(result);
            setRouteError(null);
            
            // Fit map to route bounds
            if (mapInstance && result.routes && result.routes[0] && result.routes[0].bounds) {
              try {
                mapInstance.fitBounds(result.routes[0].bounds);
              } catch (e) {
                console.error('Error fitting bounds to route:', e);
              }
            }
          } else {
            console.error(`GoogleMapComponent: Error fetching directions. Status: ${status}`, result);
            setInternalDirections(null);
            setRouteError(`Error fetching directions: ${status}`);
          }
        }
      );
    } catch (error: any) {
      console.error('Error in directions calculation effect:', error);
      setRouteError(`Error calculating directions: ${error.message || 'Unknown error'}`);
    }
  }, [pickupCoords, destinationCoords, mapInstance, mapLoaded]);
  // Uncomment this if you need to use waypoints
  /*
  const waypoints = internalDirections?.routes?.[0]?.legs.flatMap(leg =>
    leg.via_waypoints?.map((latLng: google.maps.LatLng) => { 
      if (latLng && typeof latLng.lat === 'function' && typeof latLng.lng === 'function') {
        return { lat: latLng.lat(), lng: latLng.lng() };
      }
      return null;
    }).filter(Boolean) as { lat: number; lng: number }[] || []
  ) || [];
  */

  const handleCenterChanged = () => {
    if (mapInstance) {
      const newCenter = mapInstance.getCenter();
      if (newCenter) {
        const newCenterLiteral = { lat: newCenter.lat(), lng: newCenter.lng() };
        if (newCenterLiteral.lat !== currentMapCenter.lat || newCenterLiteral.lng !== currentMapCenter.lng) {
          setCurrentMapCenter(newCenterLiteral);
        }
      }
    }
  };

  const handleZoomChanged = () => {
    if (mapInstance) {
      const newZoom = mapInstance.getZoom();
      if (newZoom !== undefined && newZoom !== currentZoom) {
        setCurrentZoom(newZoom);
      }
    }
  };

  return (
    <div className="relative w-full flex-1">
      <GoogleMap
          mapContainerClassName={mapContainerClassName}
          center={currentMapCenter}
          zoom={currentZoom}
          options={{
            mapTypeControl: false,
            fullscreenControl: true,
            streetViewControl: false,
            disableDefaultUI: false,
            clickableIcons: true,
            gestureHandling: 'cooperative', // Requires Ctrl+scroll to zoom, allows normal page scrolling
            disableDoubleClickZoom: false,
          }}          onLoad={map => {
            console.log('GoogleMap component load event triggered');
            if (!map || typeof map.setCenter !== 'function') {
              console.error('Invalid map instance provided to onLoad');
              return;
            }
            
            // Add a delay to ensure the map is fully initialized
            setTimeout(() => {
              try {
                mapRef.current = map;
                setMapInstance(map);
                
                // Make sure we have valid center coordinates
                const centerToUse = isValidCoordinate(currentMapCenter) 
                  ? currentMapCenter 
                  : DEFAULT_FALLBACK_CENTER;
                
                // Safely set center and zoom
                map.setCenter(centerToUse);
                map.setZoom(currentZoom || 12); // Default to zoom 12 if undefined
                
                console.log('Map initialization successful', {
                  center: centerToUse,
                  zoom: currentZoom
                });
                
                setMapLoaded(true);
              } catch (error) {
                console.error('Error during map initialization:', error);
                // Try one more time
                setTimeout(() => {
                  try {
                    if (map && typeof map.setCenter === 'function') {
                      map.setCenter(currentMapCenter || DEFAULT_FALLBACK_CENTER);
                      setMapLoaded(true);
                    }
                  } catch (retryError) {
                    console.error('Final attempt to initialize map failed:', retryError);
                  }
                }, 500);
              }
            }, 300);          }}          onUnmount={() => {
            console.log('GoogleMap component unmounting, performing deep cleanup');
            
            // Force clear any directions
            try {
              if (internalDirections) {
                console.log('Clearing directions on unmount');
                setInternalDirections(null);
              }
            } catch (error) {
              console.error('Error clearing directions on unmount:', error);
            }
            
            // Clear error state
            setRouteError(null);
            
            // Clear map instance
            mapRef.current = null;
            setMapInstance(null);
            setMapLoaded(false);
          }}onCenterChanged={handleCenterChanged}
          onZoomChanged={handleZoomChanged}
        >          {mapLoaded && mapInstance && (
            <>
              {/* Only render directions if we have both valid endpoints and directions data */}
              {pickupCoords && 
               destinationCoords && 
               isValidCoordinate(pickupCoords) && 
               isValidCoordinate(destinationCoords) && 
               internalDirections && 
               internalDirections.routes && 
               internalDirections.routes.length > 0 ? (
                <DirectionsRenderer
                  directions={internalDirections}
                  options={{
                    polylineOptions: {
                      strokeColor: "#4F46E5",
                      strokeWeight: 5,
                    },
                    suppressMarkers: true,
                  }}
                />
              ) : null}
              {/* Only render markers if coordinates are valid */}
              {pickupCoords && isValidCoordinate(pickupCoords) && (
                <Marker
                  position={pickupCoords}
                  title={pickupAddress || 'Pickup Location'}
                  label={{
                    text: 'P',
                    color: '#ffffff',
                    className: 'font-bold'
                  }}
                  options={{
                    optimized: true,
                    zIndex: 100
                  }}
                />
              )}
              
              {destinationCoords && isValidCoordinate(destinationCoords) && (
                <Marker
                  position={destinationCoords}
                  title={destinationAddress || 'Destination Location'}
                  label={{
                    text: 'D',
                    color: '#ffffff',
                    className: 'font-bold'
                  }}
                  options={{
                    optimized: true,
                    zIndex: 99
                  }}
                />
              )}
            </>
          )}
      </GoogleMap>
    </div>
  );
};

export default GoogleMapComponent;
